import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportData } from "@/api/report.api";

export function ReportViewer({ report, isLoading }: { report: ReportData | undefined; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!report) {
    return (
      <p className="rounded-md border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
        Choose report options above to generate a report.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-semibold">{report.title}</h2>
        <p className="text-sm text-muted-foreground">{report.subtitle}</p>
        <p className="text-xs text-muted-foreground">
          Generated {format(new Date(report.generatedAt), "dd MMM yyyy, HH:mm")}
        </p>
      </div>

      {report.summary.length > 0 && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-4">
          {report.summary.map((item) => (
            <div key={item.label}>
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="font-data text-lg font-semibold">{item.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              {report.columns.map((col) => (
                <th key={col} className="whitespace-nowrap px-4 py-3 font-medium">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 ? (
              <tr>
                <td colSpan={report.columns.length} className="px-4 py-10 text-center text-muted-foreground">
                  No records in this period.
                </td>
              </tr>
            ) : (
              report.rows.map((row, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  {row.map((cell, j) => (
                    <td key={j} className="whitespace-nowrap px-4 py-2.5">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
